import {FormProvider, useFieldArray, useForm, useWatch} from "react-hook-form";
import React, {useEffect, useMemo, useState} from "react";
import {ModalPanelForm} from "@components/Modal2023";
import FormButtons, {TraceManager} from "@components/FormButtons";
import {PresenzeGiornataPlusRecord} from "@commons/mapping/PresenzeGiornataPlusRecord";
import styles from "./PlanUL.modal.module.css";
import classNames from "classnames";
import BannerDay from "./BannerDay";
import {LavoratorePresenzeRecord} from "@commons/mapping/LavoratorePresenzeRecord";
import ComboBoxAziende from "@components/comboBox/ComboBoxAziende";
import {PresenzePianificazioneRecord} from "@commons/mapping/PresenzePianificazioneRecord";
import {AziendaRecord} from "@commons/mapping/AziendaRecord";
import RangeHH from "../../dashboardWF/RangeHH";
import {TurnoRecord} from "@commons/mapping/TurnoRecord";
import {GiustificativoRecord} from "@commons/mapping/GiustificativoRecord";
import CheckboxEx from "@components/wrapperComponents/CheckboxEx";
import {RiepilogoOrePlus} from "@commons/mapping/RiepilogoOrePlus";
import {IconMinus, IconPlus} from "@tabler/icons-react";

type FormValues = { vociGiornata: PresenzePianificazioneRecord[] }

interface PlanULProps {
    lavoratore: LavoratorePresenzeRecord,
    day: PresenzeGiornataPlusRecord,
    unitaLocale?: AziendaRecord,
    elencoUnitaLocali: AziendaRecord[],
    turni: TurnoRecord[],
    giustificativi: GiustificativoRecord[],
    onSave: (day: PresenzeGiornataPlusRecord, values: PresenzePianificazioneRecord[]) => void,
    onCancelEdit: () => void,
}

export const PlanUL: React.FC<PlanULProps> = ({
                                                  lavoratore, day, unitaLocale: ulTrascinata, elencoUnitaLocali, onSave, onCancelEdit,
                                                  turni,
                                                  giustificativi,
                                              }) => {
    const maxNumVoci = PresenzePianificazioneRecord.maxNumVoci;
    const [unitàLocaleTrascinata, setUnitàLocaleTrascinata] = useState<AziendaRecord | null>(ulTrascinata || null);
    const traceManager = useMemo<TraceManager>(() => {
        return {
            getDeleteInfo: (trace: PresenzePianificazioneRecord) => {
                return undefined;
            }
        }
    }, []);

    /**
     *  Metodo per inizializzare i valori del form sulla base delle voci della giornata indicata in <day> o in <lavoratore>
     */
    const setupDefaultValues = (vociLavoratore: PresenzePianificazioneRecord[], day: PresenzeGiornataPlusRecord, elencoUnitaLocali: AziendaRecord[]): FormValues => {
        const values: PresenzePianificazioneRecord[] = [];
        if (day) {
            let voci;
            if (day.vociPianificazione && day.vociPianificazione.length) {
                voci = day.vociPianificazione;
            } else
                voci = vociLavoratore;
            if (voci && voci.length) {
                voci.filter(v => v.dtGiornata === day.dtGiornata).forEach(vociGiornata => {
                    if (vociGiornata.idAzienda) {
                        values.push(PresenzePianificazioneRecord.factory(vociGiornata));
                    }
                });
            }
        }
        return {vociGiornata: values.length ? values : [PresenzePianificazioneRecord.factory()]};
    };

    const hook = useForm<FormValues>({
        defaultValues: setupDefaultValues([], day, elencoUnitaLocali),
        mode: 'onChange',
        reValidateMode: 'onChange'
    });
    const {getValues, setValue, control, handleSubmit, trigger} = hook;

    // useFieldArray permette di gestire form dinamici sulla base di un array di oggetti
    // vedi: https://www.youtube.com/watch?v=4MrbfGSFY2A
    const {fields, append, remove, update} = useFieldArray(
        {
            name: 'vociGiornata',
            control,
            rules: {validate: () => true}
        });

    const defaultValue = null;

    // Metodo per "pulire" il primo rigo dei giustificativi giornalieri
    const clearFirstRow = () => {
        //setValue(`vociGiornata.0.giustificativo`, null);
        setValue(`vociGiornata.0.idAzienda`, null);
        setValue(`vociGiornata.0.quantita`, 0);
        setValue(`vociGiornata.0.oraInizio`, '');
        setValue(`vociGiornata.0.oraFine`, '');
        setValue(`vociGiornata.0.giornataIntera`, false);
    }

    // watch per array giustificativi
    // la versione watch('vociGiornata') NON va usata per gli array!!!
    const vociGiornata = useWatch({
        name: 'vociGiornata',
        control
    });

    // Inizializzo valori form con giustificativo appena trascinato
    useEffect(() => {
        if (unitàLocaleTrascinata) {
            if (unitàLocaleTrascinata.idAzienda !== day.idAzienda)
                if (vociGiornata.length === 0 || !vociGiornata[0].giornataIntera) {
                    let newIndex: number | null = null;
                    vociGiornata.forEach((giu, index) => {
                        if (!giu.idAzienda && newIndex === null) {
                            newIndex = index;
                        }
                    });
                    const newGiust = PresenzePianificazioneRecord.factory({
                        ...PresenzePianificazioneRecord.getFilledInstance(lavoratore, day),
                        idAzienda: unitàLocaleTrascinata.idAzienda
                    });
                    if (newIndex === 0) {
                        update(newIndex, newGiust);
                    } else if (vociGiornata.length < maxNumVoci) {
                        append(newGiust);
                    }
                }
            setUnitàLocaleTrascinata(null);
        }
    }, [lavoratore, day, vociGiornata, unitàLocaleTrascinata, update, append, maxNumVoci]);

    // Effect per l'allineamento della edit del codice giustificativo con la combobox corrispondente
    /*useEffect(() => {
        vociGiornata.forEach((rigo, index) => {
            const {codGiu, giustificativo: giustificativoCombo} = rigo;
            if (codGiu && (!giustificativoCombo || giustificativoCombo.codGiu!.toLowerCase() !== codGiu.toLowerCase())) {
                const giuDaSelezionare = giustificativi.find(v => v.codGiu!.toLowerCase() === codGiu.toLowerCase());
                setValue(`vociGiornata.${index}.giustificativo`, giuDaSelezionare ? giuDaSelezionare : null);
            } else if (!codGiu && giustificativoCombo) {
                setValue(`vociGiornata.${index}.giustificativo`, null); // todo reset intera riga!!!
            }
        });
    }, [giustificativi, setValue, vociGiornata]);*/

    const onChangeRange = (inizio: any, fine: any, durata: any) => {
        console.log(inizio, fine, durata);
    };

    const onChangeGiornataIntera = (giornataIntera: boolean) => {
        if (giornataIntera) {
            setValue('vociGiornata.0.oraInizio', '0000');
            setValue('vociGiornata.0.oraFine', '0000');
            const hPrev = RiepilogoOrePlus.getTotaleHPrev(day.totali) / 60;
            setValue('vociGiornata.0.quantita', hPrev, {shouldValidate: true});

            let length = fields.length;
            while (length > 1)
                remove(--length);
        }
    }

    return <ModalPanelForm isOpen={!!day} formControl={control} handleDismiss={onCancelEdit}
                           header={<BannerDay day={day} lavoratore={lavoratore} giustificativi={giustificativi} turni={turni} name="Pannello Unità Locali"
                                              anomalia={day.anomalia}
                                              page='Pianificazione'/>}>
        <FormProvider {...hook}>
            <form className="p-2 bg-white shadow-md rounded xl:min-w-[1130px]"
                  onSubmit={handleSubmit(values => onSave(day, values.vociGiornata.map(v => {
                      return {...v, giustificativo: undefined}
                  })))}>
                <div className="min-h-[480px] flex flex-col">
                    <div className="mb-auto">
                        {fields.map((field, index) => {
                            const isFirst = index === 0;
                            const isLast = index === fields.length - 1;
                            const showAdd = fields.length < maxNumVoci && !vociGiornata[0].giornataIntera;
                            //const giustificativo = watch(`vociGiornata.${index}.giustificativo`);
                            return <section key={field.id}>
                                <div>
                                    <div className="flex flex-row items-start">
                                        <div className={classNames('mx-2', isFirst ? 'basis-4/12' : 'basis-5/12')}>
                                            <ComboBoxAziende<FormValues> label="Unità locale/luogo svolgimento attività"
                                                                         control={control} name={`vociGiornata.${index}.idAzienda`}
                                                                         defaultValue={defaultValue}
                                                                         rules={{required: (vociGiornata[index] && (vociGiornata[index].oraInizio || vociGiornata[index].oraFine)) ? 'Campo obbligatorio' : false}}
                                                                         data={elencoUnitaLocali.filter(AziendaRecord.isUL).filter(azienda => azienda.idAzienda !== day.idAzienda)}
                                                                         isLoading={false}
                                                                         displayValueFn={(v: AziendaRecord, elenco?: AziendaRecord[]) => elenco && v ? v.unitaLocale! : ''}

                                            />
                                        </div>
                                        {isFirst && <div className="basis-1/12 flex flex-row items-end min-h-[62px]">
                                            <CheckboxEx label="Giornata intera" name="vociGiornata.0.giornataIntera" control={control}
                                                        onToggle={onChangeGiornataIntera}/>
                                        </div>}

                                        <RangeHH<FormValues> control={control}
                                                             trigger={trigger}
                                                             getValues={getValues}
                                                             setValue={setValue}
                                                             optionalFields={!vociGiornata[index] || vociGiornata[index].idAzienda === null || vociGiornata[0].giornataIntera}
                                                             nameInizio={`vociGiornata.${index}.oraInizio`}
                                                             nameFine={`vociGiornata.${index}.oraFine`}
                                                             nameQuantita={`vociGiornata.${index}.quantita`}
                                                             validate={v => (!vociGiornata[index] || vociGiornata[index].idAzienda === null) || (v && v > 0) || 'Quantità non valida!'}
                                                             errorPortalId={`errorQuantita${index}`}
                                                             onChangeRange={onChangeRange}
                                                             disableFields={vociGiornata[0].giornataIntera}
                                        >
                                            {(hhInizio, hhFine, quantita) => {
                                                return <>
                                                    <div className="mx-2 basis-2/12">
                                                        {hhInizio}
                                                    </div>
                                                    <div className="mx-2 basis-2/12">
                                                        {hhFine}
                                                    </div>
                                                    <div className="mx-2 basis-2/12">
                                                        {quantita}
                                                    </div>
                                                </>
                                            }
                                            }
                                        </RangeHH>

                                        <div className="basis-1/12">
                                            <div className="flex flex-row items-end min-h-[62px]">
                                                <button type="button" className={classNames(styles.plusBtn, !(isLast && showAdd) && styles.hiddenBtn)} onClick={() => {
                                                    append(PresenzePianificazioneRecord.getFilledInstance(lavoratore, day));
                                                }}>
                                                    <IconPlus className="h-5 w-5 text-white"/>
                                                </button>
                                                <button type="button" className={classNames(styles.minusBtn, !(isLast && showAdd) && styles.hiddenBtn)} onClick={() => {
                                                    if (fields.length > 1)
                                                        remove(index);
                                                    else
                                                        clearFirstRow();
                                                }}>
                                                    <IconMinus className="h-5 w-5 text-white"/>
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex flex-row items-start">
                                        <div className="basis-5/12">&nbsp;</div>
                                        <div className="basis-7/12" id={`errorQuantita${index}`}></div>
                                    </div>
                                </div>
                            </section>
                        })}
                    </div>
                    <div className="">
                        <FormButtons onCancel={onCancelEdit} resource={day} traceManager={traceManager}/>
                    </div>
                </div>
            </form>
        </FormProvider>

    </ModalPanelForm>
}
export default PlanUL;